import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { adminApi } from '../../services/api';
import VoiceAILoginModal from './VoiceAILoginModal';
import AlertMessage from '../../components/shared/alertMessage';

// Lazy load the ag-grid table to avoid blocking initial page render
const SalesAgGridTable = lazy(() => import('./SalesAgGridTable'));

/**
 * VoiceAICredentialsManager Component
 *
 * Manages Voice AI Provider (RetellAI) credentials using ag-grid.
 * Allows viewing, adding, activating/deactivating, and deleting credentials.
 */
const VoiceAICredentialsManager = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });

  // Load credentials on mount
  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const response = await adminApi.voiceaiGetCredentials();
      if (response.data.success) {
        setCredentials(response.data.credentials);
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load system credentials', message: err.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = useCallback(async (credentialId, currentStatus) => {
    try {
      const response = await adminApi.voiceaiUpdateCredential({
        id: credentialId,
        is_active: !currentStatus
      });

      if (response.data.success) {
        setAlertState({ show: true, type: 'success', title: 'Credentials updated successfully', message: '' });
        loadCredentials();
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to update system credentials', message: err.response?.data?.message || err.message });
    }
  }, []);

  const handleDelete = useCallback(async (credentialId) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) {
      return;
    }

    try {
      const response = await adminApi.voiceaiDeleteCredential({ id: credentialId });

      if (response.data.success) {
        setAlertState({ show: true, type: 'success', title: 'Credential deleted successfully', message: '' });
        loadCredentials();
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to delete system credential', message: err.response?.data?.message || err.message });
    }
  }, []);

  const handleAuthSuccess = () => {
    setAlertState({ show: true, type: 'success', title: 'Authentication successful! Credential saved.', message: '' });
    loadCredentials();
    setTimeout(() => setAlertState(null), 3000);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `temp_${Date.now()}`,
      org_id: '',
      voiceai_username: '',
      jwt_token: '',
      api_token: '',
      token_type: 'dashboard_jwt',
      notes: '',
      is_active: true,
      created_at: null,
      updated_at: null,
      last_used_at: null,
      expires_at: null,
      error_count: 0,
      last_error: null,
      metadata: {},
      cookies: {},
      isNew: true
    };

    setCredentials([newRow, ...credentials]);

    // Focus on the first editable cell
    setTimeout(() => {
      if (gridApi) {
        gridApi.setFocusedCell(0, 'org_id');
        gridApi.startEditingCell({ rowIndex: 0, colKey: 'org_id' });
      }
    }, 100);
  };

  const handleCellValueChanged = async (params) => {
    const { data } = params;

    // Only save if it's a new row and has required fields
    if (data.isNew && data.org_id && data.voiceai_username && (data.jwt_token || data.api_token)) {
      try {
        const response = await adminApi.voiceaiSaveCredentials({
          org_id: data.org_id,
          voiceai_username: data.voiceai_username,
          jwt_token: data.jwt_token || undefined,
          api_token: data.api_token || undefined,
          token_type: data.token_type || 'dashboard_jwt',
          notes: data.notes || ''
        });

        if (response.data.success) {
        setAlertState({ show: true, type: 'error', title: 'Credential saved successfully', message: '' });
          loadCredentials();
        }
      } catch (err) {
        setAlertState({ show: true, type: 'error', title: 'Failed to save system credential', message: err.response?.data?.message || err.message });
        // Remove the failed row
        setCredentials(credentials.filter(c => c.id !== data.id));
      }
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleCancelNewRow = useCallback((tempId) => {
    setCredentials(prev => prev.filter(c => c.id !== tempId));
  }, []);

  const handleSaveNewRow = useCallback(async (data) => {
    if (!data.org_id || !data.voiceai_username || (!data.jwt_token && !data.api_token)) {
      setAlertState({ show: true, type: 'error', title: 'Details missing', message: 'Please fill in org_id, username, and at least one token' });
      return;
    }

    try {
      const response = await adminApi.voiceaiSaveCredentials({
        org_id: data.org_id,
        voiceai_username: data.voiceai_username,
        jwt_token: data.jwt_token || undefined,
        api_token: data.api_token || undefined,
        token_type: data.token_type || 'dashboard_jwt',
        notes: data.notes || ''
      });

      if (response.data.success) {
        setAlertState({ show: true, type: 'success', title: 'Credential created successfully', message: '' });
        loadCredentials();
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to save system credential', message: err.response?.data?.message || err.message });
    }
  }, []);

  const handleEditRow = useCallback((credentialId) => {
    setEditingRowId(credentialId);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingRowId(null);
    loadCredentials(); // Reload to discard changes
  }, []);

  const handleSaveEdit = useCallback(async (data) => {
    try {
      // Check if tokens are masked/truncated (contain "..." or "***")
      const isMaskedJWT = data.jwt_token && (data.jwt_token.includes('...') || data.jwt_token.includes('***'));
      const isMaskedAPI = data.api_token && (data.api_token.includes('...') || data.api_token.includes('***'));

      const payload = {
        id: data.id,  // Pass ID to update existing record
        org_id: data.org_id,
        voiceai_username: data.voiceai_username,
        token_type: data.token_type || 'dashboard_jwt',
        notes: data.notes || ''
      };
      // Only include tokens if they're NOT masked (i.e., user entered a new token)
      if (data.jwt_token && !isMaskedJWT) {
        payload.jwt_token = data.jwt_token;
      }
      if (data.api_token && !isMaskedAPI) {
        payload.api_token = data.api_token;
      }
      const response = await adminApi.voiceaiSaveCredentials(payload);

      if (response.data.success) {
        setAlertState({ show: true, type: 'success', title: 'Credentials updated successfully', message: '' });
        setEditingRowId(null);
        loadCredentials();
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to update system credential', message: err.response?.data?.message || err.message });
    }
  }, []);

  // Action cell renderer
  const ActionCellRenderer = useCallback((props) => {
    const { data, node, api } = props;
    // Helper to get current row data from grid (includes edited values)
    const getCurrentRowData = () => {
      // Stop editing to commit any in-progress cell edits
      api.stopEditing();
      // Get the current data from the node
      return node.data;
    };

    // Show save/cancel buttons for new unsaved rows
    if (data.isNew) {
      return (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleSaveNewRow(getCurrentRowData())}
            title="Save"
          >
            <i className="fas fa-save"></i>
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => handleCancelNewRow(data.id)}
            title="Cancel"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      );
    }

    // Show save/cancel buttons for rows being edited
    if (editingRowId === data.id) {
      return (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleSaveEdit(getCurrentRowData())}
            title="Save Changes"
          >
            <i className="fas fa-save"></i>
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleCancelEdit}
            title="Cancel"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      );
    }

    // Regular action buttons for saved rows
    return (
      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-info"
          onClick={() => handleEditRow(data.id)}
          title="Edit"
        >
          <i className="fas fa-edit"></i>
        </button>
        <button
          className={`btn border-0 ${data.is_active ? 'btn-outline-success' : 'btn-outline-secondary'}`}
          onClick={() => handleToggleActive(data.id, data.is_active)}
          title={data.is_active ? 'Click to Deactivate' : 'Click to Activate'}
        >
          <i className={`fas fa-${data.is_active ? 'toggle-on' : 'toggle-off'}`}></i>
        </button>
        <button
          className="btn btn-sm btn-danger"
          onClick={() => handleDelete(data.id)}
          title="Delete"
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>
    );
  }, [handleSaveNewRow, handleCancelNewRow, handleToggleActive, handleDelete, handleEditRow, handleSaveEdit, handleCancelEdit, editingRowId]);

  // Status cell renderer
  const StatusCellRenderer = useCallback((props) => {
    const isActive = props.value;
    return (
      <span className={`badge bg-${isActive ? 'success' : 'secondary'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  }, []);

  // Date cell renderer
  const DateCellRenderer = useCallback((props) => {
    if (!props.value) return '-';
    return new Date(props.value).toLocaleString();
  }, []);

  // JSON cell renderer
  const JSONCellRenderer = useCallback((props) => {
    if (!props.value || (typeof props.value === 'object' && Object.keys(props.value).length === 0)) {
      return '-';
    }
    return (
      <div className="text-truncate" title={JSON.stringify(props.value, null, 2)}>
        {JSON.stringify(props.value)}
      </div>
    );
  }, []);

  // Token cell renderer (truncated with tooltip)
  const TokenCellRenderer = useCallback((props) => {
    if (!props.value) return '-';
    const truncated = props.value.length > 30 ? props.value.substring(0, 30) + '...' : props.value;
    return (
      <div className="text-truncate font-monospace small" title={props.value}>
        {truncated}
      </div>
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Organization ID',
      field: 'org_id',
      flex: 1,
      minWidth: 150,
      editable: (params) => params.data.isNew || editingRowId === params.data.id,
    },
    {
      headerName: 'Username',
      field: 'voiceai_username',
      flex: 1,
      minWidth: 200,
      editable: (params) => params.data.isNew || editingRowId === params.data.id,
    },
    {
      headerName: 'JWT Token',
      field: 'jwt_token',
      flex: 2,
      minWidth: 250,
      editable: (params) => params.data.isNew || editingRowId === params.data.id,
      cellRenderer: TokenCellRenderer,
    },
    {
      headerName: 'API Token',
      field: 'api_token',
      flex: 2,
      minWidth: 250,
      editable: (params) => params.data.isNew || editingRowId === params.data.id,
      cellRenderer: TokenCellRenderer,
    },
    {
      headerName: 'Token Type',
      field: 'token_type',
      width: 130,
      editable: (params) => params.data.isNew || editingRowId === params.data.id,
    },
    {
      headerName: 'Status',
      field: 'is_active',
      cellRenderer: StatusCellRenderer,
      width: 100,
      editable: false,
    },
    {
      headerName: 'Created',
      field: 'created_at',
      cellRenderer: DateCellRenderer,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      cellRenderer: DateCellRenderer,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Last Used',
      field: 'last_used_at',
      cellRenderer: DateCellRenderer,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Expires At',
      field: 'expires_at',
      cellRenderer: DateCellRenderer,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Error Count',
      field: 'error_count',
      width: 120,
      editable: false,
    },
    {
      headerName: 'Last Error',
      field: 'last_error',
      flex: 2,
      minWidth: 200,
      editable: false,
      cellRenderer: (props) => {
        if (!props.value) return '-';
        return (
          <div className="text-truncate text-danger" title={props.value}>
            {props.value}
          </div>
        );
      },
    },
    {
      headerName: 'Notes',
      field: 'notes',
      flex: 2,
      minWidth: 200,
      editable: (params) => params.data.isNew || editingRowId === params.data.id,
    },
    {
      headerName: 'Metadata',
      field: 'metadata',
      flex: 2,
      minWidth: 200,
      editable: false,
      cellRenderer: JSONCellRenderer,
    },
    {
      headerName: 'Cookies',
      field: 'cookies',
      flex: 2,
      minWidth: 200,
      editable: false,
      cellRenderer: JSONCellRenderer,
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionCellRenderer,
      width: 140,
      sortable: false,
      filter: false,
      editable: false,
      pinned: 'right',
    }
  ], [editingRowId, ActionCellRenderer, StatusCellRenderer, DateCellRenderer, JSONCellRenderer, TokenCellRenderer]);

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: false,
  };

  return (
    <>
      <VoiceAILoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onSuccess={handleAuthSuccess}
      />
        {alertState.show && (
          <AlertMessage
            type={alertState.type}
            title={alertState.title}
            message={alertState.message}
            dismissible={true}
            onDismiss={() => setAlertState({ ...alertState, show: false })}
            className="mb-3"
          />
        )}
        <div className="page-title-box d-flex align-items-center justify-content-between">
          <div className="col-8">
            <h4 className="text-lg">Credentials Manager</h4>
            <p className="text-muted">Central resource for managing your VoiceAI provider&apos;s organizations and credentials</p>
          </div>
          <div className="col-4 d-flex justify-content-end">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin/sales">Sales</a></li>
                <li className="breadcrumb-item"><a href="/admin/sales/organizations">Organizations</a></li>
                <li className="breadcrumb-item active">Credentials</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center p-3">
          <div>
            <h4 className="mb-sm-0 font-size-18"></h4>
            <strong className="mb-0">
              Available Credentials
            </strong>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              onClick={handleAddRow}
              title="Add credential manually"
            >
              <i className="fas fa-table me-2"></i>
              Add Manually
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowLoginModal(true)}
              title="Add credential via login"
            >
              <i className="fas fa-sign-in-alt me-2"></i>
              Login & Add
            </button>
          </div>
        </div>  
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
              <Suspense fallback={
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading table...</span>
                  </div>
                </div>
              }>
                <SalesAgGridTable
                  rowData={credentials}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  pagination={true}
                  paginationPageSize={10}
                  domLayout='normal'
                  onGridReady={onGridReady}
                  onCellValueChanged={handleCellValueChanged}
                />
              </Suspense>
          )}

          <div className="mt-3">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              <strong>Add Row:</strong> Manually enter credentials (org_id, username, jwt_token/api_token required). Click <i className="fas fa-save"></i> to save.
              <strong className="ms-3">Login & Add:</strong> Authenticate via RetellAI login flow (auto-saves credentials).
              <br/>
              <i className="fas fa-edit me-1 mt-1"></i>
              <strong>Edit:</strong> Click <i className="fas fa-edit"></i> to edit any credential. Modify fields and click <i className="fas fa-save"></i> to save changes or <i className="fas fa-times"></i> to cancel.
              <br/>
              <i className="fas fa-key me-1 mt-1"></i>
              All credential details displayed: tokens, status, timestamps, error tracking, metadata, and cookies.
              Scroll horizontally to view all columns.
            </small>
          </div>
        </div>
    </>
  );
};

export default VoiceAICredentialsManager;
